import BranchRequiredWrapper from "@/components/banners/BranchRequiredWrapper";
import DataTable, { Column } from "@/components/layouts/DataTable";
import DateFilterButton from "@/components/layouts/DateFilterButton";
import DeleteConfirmationDialog from "@/components/layouts/DeleteConfirmationDialog";
import FilterOptionsButton from "@/components/layouts/FilterOptionsButton";
import PageHeader from "@/components/layouts/PageHeader";
import SearchInput from "@/components/layouts/SearchInput";
import PageLoader from "@/components/loader/PageLoader";
import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import {
  faEdit,
  faSearch,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface PromotionItem {
  id: number;
  name: string;
  promotion_type: string;
  promotion_area_type: string;
  status: string;
  photo: string;
  branch_id: number;
  created_at: string;
  max_uses: number;
  code: string;
}

interface SortState {
  field: keyof PromotionItem;
  direction: "asc" | "desc";
}

interface FilterState {
  status: string;
}

export default function Promotion() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const itemsPerPage = 10;
  const { user } = useUser();
  const [promotionItems, setPromotionItems] = useState<PromotionItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortState, setSortState] = useState<SortState>({
    field: "promotion_type",
    direction: "asc",
  });
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [filterStartDate, setFilterStartDate] = useState<Date | null>(null);
  const [filterEndDate, setFilterEndDate] = useState<Date | null>(null);
  const [filterState, setFilterState] = useState<FilterState>({
    status: "",
  });
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const columns: Column<PromotionItem>[] = [
    {
      key: "name",
      header: t("back.management.menu.promotion.table.promotionName"),
      sortable: true,
    },
    {
      key: "promotion_type",
      header: t("back.management.menu.promotion.table.promotionType"),
      sortable: true,
      render: (item) => getPromotionType(item.promotion_type),
    },
    {
      key: "promotion_area_type",
      header: t("back.management.menu.promotion.table.promotionAreaType"),
      sortable: true,
    },
    {
      key: "status",
      header: t("back.management.menu.promotion.table.status"),
    },
    {
      key: "code",
      header: "Code",
      sortable: true,
    },
    {
      key: "max_uses",
      header: "Max uses",
      sortable: true,
    },
    {
      key: "created_at",
      header: t("back.management.menu.promotion.table.date"),
      sortable: true,
    },
    {
      key: "action",
      header: t("back.management.menu.promotion.table.action"),
    },
  ];

  const getPromotionType = (type: string) => {
    switch (type) {
      case "ONE_ITEM_DISCOUNT":
        return t("back.management.menu.promotion.productOrderDiscount");
      case "BUY_X_GET_Y_DISCOUNT":
        return t("back.management.menu.promotion.buyXGetYDiscount");
      case "ENTIRE_ORDER_DISCOUNT":
        return t("back.management.menu.promotion.entireOrderDiscount");
    }
  };

  const getStatusColor = (item: PromotionItem) => {
    switch (item.status) {
      case "In Stock":
        return "bg-green-500";
      case "Low Stock":
        return "bg-yellow-500";
      case "Out of Stock":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const renderMobileCard = (item: PromotionItem) => (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center">
        {item.photo && (
          <img src={item.photo} alt={item.name} className="mr-4 h-16 w-16" />
        )}
        <div>
          <p className="font-bold text-gray-900">{item.name}</p>
          <p className="text-gray-700">
            {getPromotionType(item.promotion_type)}
          </p>
          <p className="text-gray-700">Max uses: {item.max_uses}</p>
          <p className="text-gray-700">Code: {item.code}</p>
        </div>
      </div>
      <div className="flex h-full flex-col justify-between">
        <Link
          to={`/promotions/edit/${item.id}`}
          className="fa-2x mb-2 text-gray-600 hover:text-gray-800"
        >
          <FontAwesomeIcon icon={faEdit} />
        </Link>
        <button
          onClick={() => openDeleteConfirmation(item.id)}
          className="fa-2x text-red-600 hover:text-red-800"
        >
          <FontAwesomeIcon icon={faTrashAlt} />
        </button>
      </div>
    </div>
  );

  const fetchPromotionItems = async () => {
    try {
      if (!loading && axiosInstance && user?.selectedBranch) {
        let url = `/discount/promotion/?branch=${user.selectedBranch.id}&page=${currentPage}&page_size=${itemsPerPage}`;
        if (searchQuery) {
          url += `&name=${encodeURIComponent(searchQuery)}`;
        }
        if (filterStartDate) {
          const formattedStartDate = filterStartDate.toISOString();
          url += `&created_at__gt=${formattedStartDate}`;
        }
        if (filterEndDate) {
          const formattedEndDate = filterEndDate.toISOString();
          url += `&created_at__lt=${formattedEndDate}`;
        }
        if (filterState.status) {
          url += `&status=${filterState.status}`;
        }
        const response = await axiosInstance.get(url);
        const fetchedItems = response.data.results.map(
          (item: PromotionItem) => {
            const zonedDate = toZonedTime(item.created_at, "America/Chicago");
            const formattedDate = format(zonedDate, "MM/dd/yyyy, HH:mm");
            return { ...item, created_at: formattedDate };
          },
        );
        //sorting logic
        fetchedItems.sort((a: PromotionItem, b: PromotionItem) => {
          if (sortState.field === "created_at") {
            // Convert back to dates for comparison
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return sortState.direction === "asc"
              ? dateA.getTime() - dateB.getTime()
              : dateB.getTime() - dateA.getTime();
          } else {
            const aValue = a[sortState.field];
            const bValue = b[sortState.field];
            if (typeof aValue === "number" && typeof bValue === "number") {
              return sortState.direction === "asc"
                ? aValue - bValue
                : bValue - aValue;
            } else if (
              typeof aValue === "string" &&
              typeof bValue === "string"
            ) {
              return sortState.direction === "asc"
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
            }
          }
          return 0;
        });
        setPromotionItems(fetchedItems);
        setTotalItems(response.data.count);
      }
    } catch (error) {
      console.error("Error fetching promotion items:", error);
    }
  };

  const handleFilterStartDateChange = (date: Date | null) => {
    setFilterStartDate(date);
  };

  const handleFilterEndDateChange = (date: Date | null) => {
    setFilterEndDate(date);
  };

  const handleFilterChange = (field: keyof FilterState, value: string) => {
    setFilterState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleRemoveFilters = () => {
    setFilterState({
      status: "",
    });
    setCurrentPage(1);
    fetchPromotionItems();
  };

  useEffect(() => {
    const timerId = setTimeout(() => {
      fetchPromotionItems();
    }, 500);

    return () => clearTimeout(timerId);
  }, [
    currentPage,
    searchQuery,
    sortState,
    axiosInstance,
    loading,
    user,
    filterStartDate,
    filterEndDate,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      setCurrentPage(1);
      await fetchPromotionItems();
    };

    fetchData();
  }, [filterState]);

  const openDeleteConfirmation = (id: number) => {
    setItemToDelete(id);
    setDeleteConfirmationOpen(true);
  };

  const closeDeleteConfirmation = () => {
    setItemToDelete(null);
    setDeleteConfirmationOpen(false);
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      await deleteItem(itemToDelete);
      closeDeleteConfirmation();
    }
  };

  const deleteItem = async (id: number) => {
    try {
      if (!loading && axiosInstance) {
        await axiosInstance.delete(`/discount/promotion/${id}`);
        fetchPromotionItems();
        toast.success("Promotion deleted successfully.");
      }
    } catch (error) {
      console.error("Error deleting promotion item:", error);
      toast.error("Failed to delete promotion.");
    }
  };

  const handleSortChange = (field: keyof PromotionItem) => {
    setSortState((prevState) => ({
      field,
      direction:
        prevState.field === field && prevState.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const downloadCSV = () => {
    const csvRows = [
      ["ID", "Item Name", "Description", "Price"],

      ...promotionItems.map((item) => [
        item.id,
        item.promotion_type,
        item.promotion_area_type,
        item.status,
        item.created_at,
        item.name,
      ]),
    ];

    const csvData = csvRows.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute("download", "promotion.csv");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <PageLoader isFetching={isFetching}>
      <BranchRequiredWrapper>
        <div className="container mx-auto px-4 py-2">
          {/* Title and Action Buttons */}
          <PageHeader
            title={t("back.management.menu.promotion.title")}
            exportLabel={t("back.management.menu.promotion.export")}
            addLabel={t("back.management.menu.promotion.addpromotion")}
            onExport={downloadCSV}
            onAdd={() => navigate("/promotions/add")}
          />

          <DeleteConfirmationDialog
            isOpen={deleteConfirmationOpen}
            onClose={closeDeleteConfirmation}
            onConfirm={confirmDelete}
            title={t("back.management.menu.promotion.deleteConfirmation.title")}
            message={t(
              "back.management.menu.promotion.deleteConfirmation.message",
            )}
          />
          <div className="desktop-layout">
            <div className="mb-4 flex items-center justify-between">
              <div className="relative mr-4 max-w-md flex-1">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                />
                <SearchInput
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={t("back.management.menu.product.searchProduct")}
                />
              </div>
              <div className="relative">
                <DateFilterButton
                  onStartDateChange={handleFilterStartDateChange}
                  onEndDateChange={handleFilterEndDateChange}
                  startDate={filterStartDate}
                  endDate={filterEndDate}
                  labelText={t("back.management.menu.product.selectdate")}
                />

                <FilterOptionsButton
                  filterOptions={[
                    {
                      label: t("back.management.menu.promotion.status"),
                      value: filterState.status,
                      onChange: (value) => handleFilterChange("status", value),
                      placeholder: t(
                        "back.management.menu.promotion.selectStatus",
                      ),
                      type: "select",
                      options: [
                        {
                          value: "Active",
                          label: t("back.management.menu.promotion.active"),
                        },
                        {
                          value: "Inactive",
                          label: t("back.management.menu.promotion.inactive"),
                        },
                        {
                          value: "Draft",
                          label: t("back.management.menu.promotion.draft"),
                        },
                      ],
                    },
                  ]}
                  onRemoveFilters={handleRemoveFilters}
                />
              </div>
            </div>

            <DataTable
              data={promotionItems}
              columns={columns}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              totalItems={totalItems}
              sortState={sortState}
              onSortChange={handleSortChange}
              onPageChange={handlePageChange}
              onEdit={(id) => navigate(`/promotions/edit/${id}`)}
              onDelete={openDeleteConfirmation}
              getStatusColor={getStatusColor}
              renderMobileCard={renderMobileCard}
            />
          </div>

          <div className="mobile-layout">
            <div className="mb-4 flex flex-col">
              <div className="relative mb-4 max-w-md flex-1">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                />
                <SearchInput
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={t(
                    "back.management.menu.promotion.searchPromotion",
                  )}
                />
              </div>
              <div className="relative mb-4">
                <DateFilterButton
                  onStartDateChange={handleFilterStartDateChange}
                  onEndDateChange={handleFilterEndDateChange}
                  startDate={filterStartDate}
                  endDate={filterEndDate}
                  labelText={t("back.management.menu.promotion.selectdate")}
                />

                <FilterOptionsButton
                  filterOptions={[
                    {
                      label: t("back.management.menu.promotion.status"),
                      value: filterState.status,
                      onChange: (value) => handleFilterChange("status", value),
                      placeholder: t(
                        "back.management.menu.promotion.selectStatus",
                      ),
                      type: "select",
                      options: [
                        {
                          value: "Active",
                          label: t("back.management.menu.promotion.active"),
                        },
                        {
                          value: "Inactive",
                          label: t("back.management.menu.promotion.inactive"),
                        },
                        {
                          value: "Draft",
                          label: t("back.management.menu.promotion.draft"),
                        },
                      ],
                    },
                  ]}
                  onRemoveFilters={handleRemoveFilters}
                />
              </div>
            </div>

            <DataTable
              data={promotionItems}
              columns={columns}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              totalItems={totalItems}
              sortState={sortState}
              onSortChange={handleSortChange}
              onPageChange={handlePageChange}
              onEdit={(id) => navigate(`/promotions/edit/${id}`)}
              onDelete={openDeleteConfirmation}
              getStatusColor={getStatusColor}
              renderMobileCard={renderMobileCard}
            />
          </div>
        </div>
      </BranchRequiredWrapper>
    </PageLoader>
  );
}
